import React, { useEffect, useState } from "react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { PULSE_METHOD } from "@/lib/pulseConstants";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, X } from "lucide-react";

/**
 * Mobile Pulse Method — a vertical numbered timeline that replaces the
 * five stacked bordered cards on small screens. Each step shows its
 * number, title and one concise sentence by default. A tap on any step
 * reveals an expanded detail region beneath it (single-open behaviour).
 * Dividers separate steps instead of using large bordered containers so
 * the whole method fits within ~2 mobile screens.
 */
export default function MobilePulseMethodTimeline() {
    const [openIdx, setOpenIdx] = useState(null);

    useEffect(() => {
        if (openIdx === null) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setOpenIdx(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [openIdx]);

    return (
        <section
            className="section-pad relative border-y border-white/10 bg-[#0A0B10]"
            data-testid="home-pulse-method-mobile-section"
        >
            <div className="container-editorial">
                <div className="mb-8">
                    <SectionEyebrow index="05" label="The Pulse Method" />
                    <Reveal>
                        <h2 className="display-2 text-white text-balance">
                            From brand objective
                            <br />
                            to <span className="text-[#FF2A2A]">cultural moment.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <p className="body-md mt-4">
                            A focused process that connects strategy, audience, experience and amplification.
                        </p>
                    </Reveal>
                </div>

                <ol className="relative" data-testid="pulse-method-mobile-list">
                    {PULSE_METHOD.map((s, i) => {
                        const isOpen = openIdx === i;
                        const isLast = i === PULSE_METHOD.length - 1;
                        const contentId = `pulse-method-step-${s.step}`;
                        return (
                            <li
                                key={s.step}
                                className={`relative flex items-start gap-4 py-5 ${isLast ? "" : "border-b border-white/10"}`}
                                data-testid={`pulse-method-mobile-step-${s.step}`}
                            >
                                {/* Prominent number */}
                                <div className="shrink-0 w-14">
                                    <span className={`font-display text-4xl leading-none ${isOpen ? "text-[#FF2A2A]" : "text-white/70"}`}>
                                        {s.step}
                                    </span>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="font-display text-xl leading-tight text-white">
                                                {s.title}
                                            </h3>
                                            <p className="text-sm text-white/70 leading-relaxed mt-1.5">
                                                {s.body}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setOpenIdx(isOpen ? null : i)}
                                            aria-expanded={isOpen}
                                            aria-controls={contentId}
                                            aria-label={`${isOpen ? "Hide" : "Show"} detail for ${s.title}`}
                                            data-testid={`pulse-method-mobile-toggle-${s.step}`}
                                            className={`h-11 w-11 -mr-1 rounded-full border flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black ${isOpen ? "border-[#FF2A2A]/60 bg-[#FF2A2A] text-black" : "border-white/15 text-white/70 hover:text-white"}`}
                                        >
                                            {isOpen ? <X size={16} /> : <Plus size={16} />}
                                        </button>
                                    </div>

                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                key="detail"
                                                id={contentId}
                                                role="region"
                                                aria-live="polite"
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                                className="overflow-hidden"
                                                data-testid={`pulse-method-mobile-detail-${s.step}`}
                                            >
                                                <div className="mt-3 rounded-xl border border-white/10 bg-[#0B0C10] p-4 text-sm text-white/80 leading-relaxed">
                                                    {s.body}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </section>
    );
}
